/**
 * Типы для Auth module
 * Портировано из lib/features/auth/domain/entities/
 */

/** Учетные данные для входа */
export interface LoginRequest {
  email: string
  password: string
}

/** JWT токены */
export interface AuthTokens {
  accessToken: string
  refreshToken: string
}

/**
 * Что экран принятия приглашения знает о приглашении до входа (DEV-1308).
 *
 * Только почта: роли приходят идентификаторами, а справочник ролей закрыт
 * правом `roles:read` — назвать их анониму нечем.
 */
export interface InvitationPreview {
  email: string
}

/** Данные, которыми приглашённый заводит себе учётную запись. */
export interface AcceptInvitationPayload {
  token: string
  firstName: string
  lastName: string
  password: string
}

/** Роли пользователя */
export type UserRole =
  | 'ceo'
  | 'admin'
  // Роль администратора админ-панели, заводится миграцией auth наравне с ADMIN.
  | 'admin_user'
  | 'hos'
  | 'manager'
  | 'support_manager'
  | 'agent'
  | 'user'
  | 'marketer'
  // DEV-1021. Менеджер каталога: видит только раздел «Объекты», управляет
  // материалами ЖК. Никаких клиентов/сделок/настроек.
  | 'project_manager'
  // DEV-1167. Внешний интегратор партнёрского портала документации. В CRM ему
  // делать нечего — см. CRM_ALLOWED_ROLES.
  | 'partner'

/** Пользователь */
export interface User {
  id: string
  email: string
  name: string
  first_name?: string
  last_name?: string
  status: number
  roles: UserRole[]
  /**
   * Эффективные права: роли плюс индивидуальные гранты, уже развёрнутые
   * сервером. Отдаются и в /me, и в ответе логина (DEV-1257).
   *
   * ⚠️ Поля может не быть: protojson не сериализует пустой repeated, поэтому
   * человек без единого права получает объект вообще без ключа. Отличить это
   * от «бэкенд поле не отдаёт» по телу невозможно — страховка выката висит на
   * статусе карты разделов (legacy), а не на наличии поля.
   */
  permissions?: string[]
  /** Строка RFC3339 (DEV-1268). */
  created_at?: string
  /** Строка RFC3339 (DEV-1268). */
  updated_at?: string
  avatar?: string
}

/** Ответ на запрос логина */
export interface LoginResponse {
  tokens: AuthTokens
  user: User
}

/** Ответ на запрос обновления токена */
export interface RefreshTokenResponse {
  tokens: AuthTokens
}

/** Настройки пользователя (из auth-service) */
export interface UserSettings {
  user_id: string
  timezone: string
  avatar_url?: string
  telegram_chat_id?: number
  telegram_username?: string
  telegram_bot_verified: boolean
  whatsapp_phone?: string
  languages?: string[]
  /** Строка RFC3339 (DEV-1268). */
  created_at?: string
  /** Строка RFC3339 (DEV-1268). */
  updated_at?: string
}

/** Параметры обновления настроек пользователя */
export interface UpdateUserSettingsParams {
  timezone?: string
  avatar_url?: string
  whatsapp_phone?: string
  languages?: string[]
  clear_languages?: boolean
}

/** Состояние аутентификации */
export type AuthState = 'unauthenticated' | 'loading' | 'authenticated' | 'error'

/** Маппинг ролей на человекочитаемый текст */
export const ROLE_LABELS: Record<UserRole, string> = {
  // DEV-464. CEO — генеральный директор. Реальные права у него от парной роли admin,
  // эта роль-метка нужна для отображения и попадания в список менеджеров воронки.
  ceo: 'Генеральный директор',
  admin: 'Администратор',
  admin_user: 'Администратор админ-панели',
  hos: 'Руководитель отдела продаж',
  manager: 'Менеджер',
  // DEV-699. Сопровождающий менеджер: видит только сопровождаемые сделки,
  // не создаёт/не отменяет/не удаляет сделки.
  support_manager: 'Сопровождающий менеджер',
  agent: 'Агент',
  user: 'Пользователь',
  marketer: 'Маркетолог',
  // DEV-1021. Менеджер каталога — управляет объектами и материалами ЖК.
  project_manager: 'Менеджер каталога',
  // DEV-1167. Внешний интегратор: только партнёрский портал документации.
  partner: 'Партнёр',
}

/**
 * Приоритет ролей для отображения: показываем самую старшую роль пользователя.
 * DEV-464. ceo выше admin: у гендиректора пара ролей admin+ceo, и в UI он должен
 * отображаться как «Генеральный директор», а не «Администратор».
 */
export const ROLE_PRIORITY: UserRole[] = [
  'ceo',
  'admin',
  'admin_user',
  'hos',
  'marketer',
  'manager',
  'support_manager',
  // DEV-1021. Менеджер каталога — узкая роль, ниже менеджеров, выше агента.
  'project_manager',
  'agent',
  // DEV-1167. Партнёр — внешний контрагент, ниже любой внутренней роли.
  'partner',
  'user',
]

/**
 * Роли, которым открыт вход в CRM (DEV-1167, DEV-1177).
 *
 * Это allowlist, а не запрет партнёра: роли заводятся в auth и приезжают сюда
 * готовым списком, поэтому следующая внешняя роль при запрете-по-имени снова
 * оказалась бы внутри по умолчанию. Здесь наоборот — незнакомая роль внутрь не
 * попадает, пока её сюда явно не добавят.
 *
 * Состав повторяет то, что пускалось до появления гварда, минус partner: у
 * партнёра тот же логин и та же форма входа, но своё приложение (docs).
 */
export const CRM_ALLOWED_ROLES: readonly UserRole[] = [
  'ceo',
  'admin',
  'admin_user',
  'hos',
  'manager',
  'support_manager',
  'marketer',
  'project_manager',
  'agent',
  'user',
]

/**
 * isRoleAllowedInCrm сообщает, пускать ли пользователя с такими ролями в CRM.
 *
 * Ключи ролей приходят с бэкенда в верхнем регистре и иногда с префиксом
 * `role:` — сравнение регистронезависимое и с отбрасыванием префикса. На этом
 * уже обжигались в DEV-1114, где роль-гейт из-за регистра не срабатывал никогда.
 *
 * Пользователь без ролей вовсе — пускается: так вело себя приложение до гварда,
 * и лишать доступа из-за пустого списка ролей эта задача не просит.
 */
export function isRoleAllowedInCrm(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return true

  return roles.some((role) =>
    CRM_ALLOWED_ROLES.includes(role.replace(/^role:/i, '').toLowerCase() as UserRole),
  )
}

/**
 * getPrimaryRole возвращает приоритетную роль пользователя в нижнем регистре.
 * У пользователя может быть несколько ролей, а ключи с бэкенда приходят в
 * верхнем регистре — поэтому нормализуем регистр и выбираем старшую роль, а не
 * первую попавшуюся (DEV-196).
 */
export function getPrimaryRole(roles: string[] | undefined): UserRole | '' {
  if (!roles || roles.length === 0) return ''

  const normalized = roles.map((role) => role.toLowerCase())

  for (const role of ROLE_PRIORITY) {
    if (normalized.includes(role)) return role
  }

  // Роль есть, но вне известного списка — возвращаем первую как есть.
  return (normalized[0] as UserRole) || ''
}
